import { Inject, Injectable, NgZone, PLATFORM_ID } from '@angular/core';
import { AnalyticsBrowser, Context } from '@segment/analytics-next';

import { isPlatformServer } from '@angular/common';
import { ConfigurationService, ISegmentAppConfig } from '@sondermind/configuration';
import { Logger } from '@sondermind/datadog-logging';

import { isBotUserAgent, snakeCaseTransformer } from '@sondermind/utilities/tools';

export type SegmentSchema = Record<string, unknown>;

@Injectable({
  providedIn: 'root'
})
export class SegmentService<TSchema extends SegmentSchema> {
  analytics: AnalyticsBrowser | undefined;

  constructor(
    private configurationService: ConfigurationService<ISegmentAppConfig>,
    private logger: Logger,
    @Inject(PLATFORM_ID) private platformId: Object,
    private ngZone: NgZone,
  ) {
  }

  async init(): Promise<boolean> {
    if (isPlatformServer(this.platformId)) {
      this.logger.warn('Aborting Segment Initialization because Segment not setup to run on server.');
      return false;
    }
    if (!this.configurationService.env.app?.segmentWriteKey) {
      this.logger.warn('Aborting Segment Initialization because of missing write key.');
      return false;
    }
    if (this.configurationService.env.app?.segmentDisabled) {
      this.logger.warn('Aborting Segment Initialization because it\'s been disabled.');
      return false;
    }
    if (isBotUserAgent()) {
      this.logger.info('Aborting Segment initialization for bot traffic.');
      return false;
    }
    try {
      if (!this.analytics) {
        const enablePixels = !!this.configurationService.env.app?.enableSegmentPixelDest;
        const integrations = {
          All: true,
          'Facebook Pixel': enablePixels,
          'TikTok Pixel': enablePixels,
          'Google AdWords New': enablePixels,
          'Bing Ads': enablePixels,
          'Segment.io': {
            apiHost: 'segment-api.sondermind.com/v1'
          }
        };
        // Load runs CDN / network work outside the Angular zone so it does not block isStable.
        const analyticsBrowser = this.ngZone.runOutsideAngular(() => AnalyticsBrowser.load(
          {
            writeKey: this.configurationService.env.app?.segmentWriteKey,
            cdnURL: 'https://segment.sondermind.com',
          },
          { integrations }
        ));
        await analyticsBrowser;
        this.analytics = analyticsBrowser;
      }
    } catch (error) {
      this.logger.warn('Failed to initialize Segment analytics:', error as Error);
      this.analytics = undefined;
      return false;
    }

    return true;
  }

  async identifyUser(platformAccountUuid: string): Promise<Context | void> {
    const initializationSuccessful = await this.init();
    if (!initializationSuccessful) {
      return undefined;
    }
    return this.analytics?.identify(platformAccountUuid);
  }

  async anonymousId(): Promise<string | undefined | null> {
    const initializationSuccessful = await this.init();

    if (!initializationSuccessful) {
      return undefined;
    }

    const analyticsUser = await this.analytics?.user();
    return analyticsUser?.anonymousId();
  }

  /**
   * This registers an event with segment, posting tracking data along with the name of the action.
   *
   * One important note is that segment collects anonymousId implicitly. You do not need to include that as
   * part of your changesets.
   */
  async track<T extends keyof TSchema>(event: T, data: TSchema[T]): Promise<void> {
    if (!this.analytics) {
      // if initialization fails (like when no segmentWriteKey is provided) and returns false
      // we do nothing with the tracking event/data
      if (!await this.init()) {
        return;
      }
    }

    // Automatically make the data snake case
    const snakeCaseData = snakeCaseTransformer(data);
    // if analytics exists (implying init succeeded already at any point)
    // we track the event and post the data.
    await this.analytics?.track(event.toString(), snakeCaseData as Record<string, unknown>).catch(
      (error) => {
        this.logger.error(
          `Segment: An error occurred when trying to track ${String(event)} with data ${snakeCaseData}:`, error
        );
      }
    );
  }
}
